import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// Translations for the UI
const translations = {
  fr: {
    // Landing page - Hero
    heroTitle: "Votre profil professionnel",
    heroTitleHighlight: "complet",
    heroSubtitle: "Créez un CV percutant et découvrez vos forces cachées pour vous démarquer sur le marché africain",
    
    // Section titles
    cvSectionTitle: "Construisez votre CV",
    cvSectionSubtitle: "Votre parcours, vos compétences, votre histoire professionnelle",
    assessmentSectionTitle: "Découvrez votre Soft Power",
    assessmentSectionSubtitle: "Vos forces de caractère, votre style de leadership",
    
    // CV options
    iHaveCV: "J'ai déjà un CV",
    iHaveCVDesc: "Importez via l'app EdoMatch",
    improveCV: "Améliorer mon CV",
    improveCVDesc: "Analysez et optimisez votre CV existant",
    createNewCV: "Créer mon CV",
    createNewCVDesc: "Construisez votre CV étape par étape avec notre coach IA",
    
    // Assessment option
    softPower: "Test de personnalité",
    softPowerDesc: "20 questions • 5 minutes",
    softPowerTagline: "Adapté au marché ouest-africain",
    
    // Value proposition
    whyBoth: "Pourquoi les deux ?",
    whyBothText: "Un CV montre ce que vous avez fait. Votre Soft Power révèle qui vous êtes vraiment. Ensemble, ils créent un profil complet qui parle aux recruteurs.",
    completeProfile: "Profil complet",
    completeProfileDesc: "CV + Personnalité = Document unique à partager",
    
    // CTAs
    getStarted: "Commencer",
    startTest: "Passer le test",
    fiveMinutes: "5 min",
    poweredBy: "Propulsé par l'IA • Données sécurisées",
    
    // App redirect screen
    greatNews: "Excellente nouvelle !",
    continueOnApp: "Continuez sur l'application EdoMatch",
    appDescription: "Importez et gérez vos CV directement depuis notre application mobile.",
    backToOptions: "Retour aux options",
    
    // Upload section
    uploadYourCV: "Téléchargez votre CV à améliorer",
    acceptedFormats: "Formats acceptés: PDF, DOCX, TXT",
    clickToSelect: "Cliquez pour sélectionner",
    orDragDrop: "ou glissez-déposez votre fichier",
    cancel: "Annuler",
    analyzeMyCV: "Analyser mon CV",
    analyzing: "Analyse...",
    back: "Retour",
    
    // Loading animation
    preparingSession: "Préparation de votre session",
    coachPreparing: "Votre coach CV se prépare à vous accueillir...",
    estimatedTime: "30 secondes à 1 minute",
    
    // Chat
    coachCVEdoMatch: "Coach CV EdoMatch",
    online: "en ligne",
    typing: "écrit...",
    typeMessage: "Tapez un message...",
    viewMyCV: "Voir mon CV",
    
    // Sidebar
    progression: "Progression",
    qualityBySection: "QUALITÉ PAR SECTION",
    identity: "Identité",
    contact: "Contact",
    bio: "Bio",
    objective: "Objectif",
    education: "Éducation",
    experience: "Expériences",
    skills: "Compétences",
    certifications: "Certifications",
    projects: "Projets",
    languages: "Langues",
    rewards: "Récompenses",
    
    // CV Preview
    editInstruction: "Cliquez sur un texte pour le modifier",
    downloadPDF: "Télécharger PDF",
    generatingPDF: "Génération du PDF en cours...",
    pdfSuccess: "PDF téléchargé avec succès !",
    noProfileFound: "Aucun profil trouvé",
    
    // CV Preview - Personality CTA
    discoverSoftPower: "Découvrez votre Soft Power",
    completeCVWithPersonality: "Ajoutez une dimension unique à votre CV en révélant vos forces de caractère",
    takePersonalityTest: "Passer le test de personnalité",
    personalitySection: "Profil de personnalité",
    
    // Personality Result - CV CTA
    createCVNow: "Créez votre CV maintenant",
    completeProfessionalProfile: "Combinez vos résultats avec un CV professionnel pour un profil complet",
    startCVCreation: "Créer mon CV",
    downloadCompleteProfile: "Télécharger mon profil complet",
    shareOnLinkedIn: "Partager sur LinkedIn",
    yourRetrievalCode: "Votre code de récupération",
    saveCodeInfo: "Conservez ce code pour retrouver vos résultats dans l'app EdoMatch",
    copyCode: "Copier",
    codeCopied: "Code copié !",
  },
  en: {
    // Landing page - Hero
    heroTitle: "Your complete",
    heroTitleHighlight: "professional profile",
    heroSubtitle: "Create a powerful CV and discover your hidden strengths to stand out in the African market",
    
    // Section titles
    cvSectionTitle: "Build your CV",
    cvSectionSubtitle: "Your journey, your skills, your professional story",
    assessmentSectionTitle: "Discover your Soft Power",
    assessmentSectionSubtitle: "Your character strengths, your leadership style",
    
    // CV options
    iHaveCV: "I already have a CV",
    iHaveCVDesc: "Import via EdoMatch app",
    improveCV: "Improve my CV",
    improveCVDesc: "Analyze and optimize your existing CV",
    createNewCV: "Create my CV",
    createNewCVDesc: "Build your CV step by step with our AI coach",
    
    // Assessment option
    softPower: "Personality Test",
    softPowerDesc: "20 questions • 5 minutes",
    softPowerTagline: "Adapted for West African market",
    
    // Value proposition
    whyBoth: "Why both?",
    whyBothText: "A CV shows what you've done. Your Soft Power reveals who you really are. Together, they create a complete profile that speaks to recruiters.",
    completeProfile: "Complete Profile",
    completeProfileDesc: "CV + Personality = Unique document to share",
    
    // CTAs
    getStarted: "Get started",
    startTest: "Take the test",
    fiveMinutes: "5 min",
    poweredBy: "Powered by AI • Secure data",
    
    // App redirect screen
    greatNews: "Great news!",
    continueOnApp: "Continue on the EdoMatch app",
    appDescription: "Import and manage your CVs directly from our mobile app.",
    backToOptions: "Back to options",
    
    // Upload section
    uploadYourCV: "Upload your CV to improve",
    acceptedFormats: "Accepted formats: PDF, DOCX, TXT",
    clickToSelect: "Click to select",
    orDragDrop: "or drag and drop your file",
    cancel: "Cancel",
    analyzeMyCV: "Analyze my CV",
    analyzing: "Analyzing...",
    back: "Back",
    
    // Loading animation
    preparingSession: "Preparing your session",
    coachPreparing: "Your CV coach is getting ready to welcome you...",
    estimatedTime: "30 seconds to 1 minute",
    
    // Chat
    coachCVEdoMatch: "EdoMatch CV Coach",
    online: "online",
    typing: "typing...",
    typeMessage: "Type a message...",
    viewMyCV: "View my CV",
    
    // Sidebar
    progression: "Progress",
    qualityBySection: "QUALITY BY SECTION",
    identity: "Identity",
    contact: "Contact",
    bio: "Bio",
    objective: "Objective",
    education: "Education",
    experience: "Experience",
    skills: "Skills",
    certifications: "Certifications",
    projects: "Projects",
    languages: "Languages",
    rewards: "Rewards",
    
    // CV Preview
    editInstruction: "Click on any text to edit it",
    downloadPDF: "Download PDF",
    generatingPDF: "Generating PDF...",
    pdfSuccess: "PDF downloaded successfully!",
    noProfileFound: "No profile found",
    
    // CV Preview - Personality CTA
    discoverSoftPower: "Discover your Soft Power",
    completeCVWithPersonality: "Add a unique dimension to your CV by revealing your character strengths",
    takePersonalityTest: "Take the personality test",
    personalitySection: "Personality Profile",
    
    // Personality Result - CV CTA
    createCVNow: "Create your CV now",
    completeProfessionalProfile: "Combine your results with a professional CV for a complete profile",
    startCVCreation: "Create my CV",
    downloadCompleteProfile: "Download my complete profile",
    shareOnLinkedIn: "Share on LinkedIn",
    yourRetrievalCode: "Your retrieval code",
    saveCodeInfo: "Save this code to retrieve your results in the EdoMatch app",
    copyCode: "Copy",
    codeCopied: "Code copied!",
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    // 1. Check if already saved in localStorage
    const saved = localStorage.getItem('edomatch_language');
    if (saved) return saved;
    
    // 2. Auto-detect from browser/system language
    const browserLang = navigator.language || navigator.userLanguage || 'fr';
    const detectedLang = browserLang.toLowerCase().startsWith('en') ? 'en' : 'fr';
    
    // Save detected language immediately
    localStorage.setItem('edomatch_language', detectedLang);
    
    return detectedLang;
  });

  useEffect(() => {
    localStorage.setItem('edomatch_language', language);
    // Also set document language attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['fr'][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
